import { useEffect, useState } from 'react';
import { readSession, signOut } from './auth/client-session';
import {
  getMe,
  getMySections,
  getProfile,
  getProfileMenu,
  getStartSection,
  updateTheme,
  visitSection,
} from './api/panel-api';
import Shell from './components/shell';
import { formatDateTime, initials, panelRoleLabel, sectionIcons } from './data/panel';
import {
  managersLocationKeepsAddress,
  managersPath,
  managersSectionOf,
  parseManagersLocation,
  sectionFromPath,
  sectionPath,
  sectionToOpen,
  userCardPath,
  userIdFromPath,
} from './section-url';
import LoginPage from './pages/login-page';
import ManagerCardPage from './pages/manager-card-page';
import ManagersPage from './pages/managers-page';
import './styles/panel.scss';
import ProfilePage from './pages/profile-page';
import UserCardPage from './pages/user-card-page';
import UserFormPage from './pages/user-form-page';
import UsersPage from './pages/users-page';

function App() {
  const [session, setSession] = useState(readSession);
  const [expired, setExpired] = useState(false);

  useEffect(() => {
    function expire() {
      setExpired(true);
      setSession(null);
    }
    window.addEventListener('rhm-session-expired', expire);
    return () => window.removeEventListener('rhm-session-expired', expire);
  }, []);

  async function handleSignOut() {
    await signOut();
    setExpired(false);
    setSession(null);
  }

  if (!session) {
    return (
      <LoginPage
        initialMode={expired ? 'session' : 'login'}
        onSignIn={(next) => {
          setExpired(false);
          setSession(next);
        }}
      />
    );
  }

  return <Panel onSignOut={handleSignOut} />;
}

const navKey = 'rhm-panel-nav';

function replaceSectionAddress(code) {
  const path = sectionPath(code);
  if (window.location.pathname !== path) window.history.replaceState(null, '', path);
}

function usersSectionOf(items) {
  return (items || []).find((item) => item.code === 'users' || item.title === 'Пользователи');
}

function presentSections(items) {
  const visible = (items || [])
    .filter((item) => !item.parent_code)
    .slice()
    .sort((left, right) => left.sort_order - right.sort_order)
    .map((item) => ({
      ...item,
      icon: sectionIcons[item.code] || 'info',
    }));
  return {
    work: visible.filter((item) => item.group === 'work'),
    administration: visible.filter((item) => item.group === 'administration'),
    all: visible,
  };
}

function Panel({ onSignOut }) {
  const [profile, setProfile] = useState(null);
  const [menu, setMenu] = useState(null);
  const [sections, setSections] = useState({ work: [], administration: [], all: [] });
  const [section, setSection] = useState('');
  const [lastLogin, setLastLogin] = useState('—');
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(navKey) === 'collapsed');
  const [screen, setScreen] = useState(null);
  const [managersLocation, setManagersLocation] = useState({ view: 'employees' });
  const [block, setBlock] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [ready, setReady] = useState(false);

  useEffect(() => {
    localStorage.setItem(navKey, collapsed ? 'collapsed' : 'expanded');
  }, [collapsed]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const [nextProfile, nextMenu, me] = await Promise.all([getProfile(), getProfileMenu(), getMe()]);
        if (cancelled) return;
        setProfile(nextProfile);
        setMenu(nextMenu);
        setLastLogin(formatDateTime(me.last_login_at).text);
        if (nextProfile.must_change_password) setScreen('profile');
        try {
          const [sectionList, start] = await Promise.all([getMySections(), getStartSection()]);
          if (cancelled) return;
          const known = presentSections(sectionList.items);
          const requested = sectionFromPath(window.location.pathname);
          const next = sectionToOpen(known.all, requested, start.section);
          const users = usersSectionOf(known.all);
          const managers = managersSectionOf(known.all);
          const userId = users && next === users.code
            ? userIdFromPath(window.location.pathname, users.code)
            : '';
          const nextManagers = managers && next === managers.code
            ? parseManagersLocation(window.location.pathname, managers.code)
            : { view: 'employees' };
          setSections(known);
          setSection(next);
          setManagersLocation(nextManagers);
          if (userId && !nextProfile.must_change_password) {
            setScreen({ userId });
          } else if (managersLocationKeepsAddress(nextManagers)) {
            /* nested managers address stays */
          } else {
            replaceSectionAddress(next);
          }
          if (requested && next === requested) visitSection(next).catch(() => {});
        } catch (accessError) {
          if (cancelled) return;
          if (accessError.code === 'access_not_configured' || accessError.code === 'email_not_confirmed') {
            setBlock(accessError.code);
          } else {
            setLoadError(accessError.message);
          }
        }
      } catch (error) {
        if (cancelled) return;
        if (error.code === 'email_not_confirmed' || error.code === 'access_not_configured') {
          setBlock(error.code);
        } else {
          setLoadError(error.message);
        }
      } finally {
        if (!cancelled) setReady(true);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  function openSection(code) {
    setScreen(null);
    setManagersLocation({ view: 'employees' });
    const path = sectionPath(code);
    if (section !== code || window.location.pathname !== path) {
      setSection(code);
      if (window.location.pathname !== path) window.history.pushState(null, '', path);
      if (section !== code) visitSection(code).catch(() => {});
    }
  }

  function openManagersLocation(next) {
    const managers = managersSectionOf(sections.all);
    if (!managers) return;
    setScreen(null);
    setManagersLocation(next);
    const path = managersPath(managers.code, next);
    if (window.location.pathname !== path) window.history.pushState(null, '', path);
  }

  useEffect(() => {
    function onPop() {
      if (!sections.all.length) return;
      const requested = sectionFromPath(window.location.pathname);
      const next = sectionToOpen(sections.all, requested, 'dashboard');
      const users = usersSectionOf(sections.all);
      const managers = managersSectionOf(sections.all);
      const userId = users && next === users.code
        ? userIdFromPath(window.location.pathname, users.code)
        : '';
      const nextManagers = managers && next === managers.code
        ? parseManagersLocation(window.location.pathname, managers.code)
        : { view: 'employees' };
      setSection(next);
      setManagersLocation(nextManagers);
      setScreen(userId ? { userId } : null);
      if (!userId && !managersLocationKeepsAddress(nextManagers)) replaceSectionAddress(next);
      if (next) visitSection(next).catch(() => {});
    }
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [sections]);

  async function changeTheme(theme) {
    const previous = profile.theme;
    setProfile((current) => ({ ...current, theme }));
    try {
      await updateTheme(theme);
    } catch {
      setProfile((current) => ({ ...current, theme: previous }));
    }
  }

  const menuUser = {
    firstName: (menu && menu.first_name) || (profile && profile.first_name) || '',
    lastName: (menu && menu.last_name) || (profile && profile.last_name) || '',
    role: panelRoleLabel[(menu && menu.role) || (profile && profile.role)] || '',
    initials: initials(
      (menu && menu.first_name) || (profile && profile.first_name),
      (menu && menu.last_name) || (profile && profile.last_name),
    ),
  };
  const showEnvironment = Boolean(
    (menu && menu.can_see_environment_switcher)
    || (profile && profile.can_see_environment_switcher)
    || (profile && profile.role === 'administrator'),
  );
  const current = sections.all.find((item) => item.code === section);
  const usersSection = usersSectionOf(sections.all);
  const managersSection = managersSectionOf(sections.all);

  let body;
  if (!ready) {
    body = <section className="content content--center" aria-busy="true" />;
  } else if (block) {
    body = (
      <section className="content content--center">
        <div className="greeting">
          <h1>{block === 'email_not_confirmed' ? 'Подтвердите почту' : 'Доступ не настроен, обратитесь к администратору'}</h1>
          <button type="button" className="btn" onClick={onSignOut}>Войти</button>
        </div>
      </section>
    );
  } else if (loadError && !profile) {
    body = (
      <section className="content content--center">
        <div className="greeting">
          <h1>{loadError}</h1>
        </div>
      </section>
    );
  } else if (screen === 'profile' && profile) {
    body = (
      <ProfilePage
        profile={profile}
        lastLogin={lastLogin}
        onSignOut={onSignOut}
        onSaved={(next) => {
          setProfile(next);
          setMenu((currentMenu) => ({
            ...currentMenu,
            first_name: next.first_name,
            last_name: next.last_name,
          }));
        }}
      />
    );
  } else if (screen === 'new') {
    body = (
      <UserFormPage
        sections={sections.all}
        onCancel={() => setScreen(null)}
        onCreate={() => setScreen(null)}
      />
    );
  } else if (screen && screen.userId) {
    body = (
      <UserCardPage
        userId={screen.userId}
        currentUserId={profile && profile.id}
        sections={sections.all}
        onBack={() => {
          setScreen(null);
          const path = sectionPath(usersSection.code);
          if (window.location.pathname !== path) window.history.pushState(null, '', path);
        }}
        onOpenManagers={() => {
          setScreen(null);
          if (managersSection) openSection(managersSection.code);
        }}
      />
    );
  } else if (usersSection && section === usersSection.code) {
    body = (
      <UsersPage
        onOpen={(userId) => {
          setScreen({ userId });
          const path = userCardPath(usersSection.code, userId);
          if (window.location.pathname !== path) window.history.pushState(null, '', path);
        }}
        onCreate={() => setScreen('new')}
      />
    );
  } else if (managersSection && section === managersSection.code) {
    body = managersLocation.view === 'card' && managersLocation.managerId ? (
      <ManagerCardPage
        sectionCode={managersSection.code}
        managerId={managersLocation.managerId}
        tab={managersLocation.tab}
        role={profile && profile.role}
        currentUserId={profile && profile.id}
        onLocation={openManagersLocation}
      />
    ) : (
      <ManagersPage
        sectionCode={managersSection.code}
        location={managersLocation}
        role={profile && profile.role}
        onLocation={openManagersLocation}
      />
    );
  } else if (!current || current.code === 'dashboard' || current.title === 'Дашборд') {
    body = (
      <section className="content content--center" aria-label="Дашборд">
        <div className="greeting">
          <h1>Здравствуйте, {menuUser.firstName}</h1>
          <p>Выберите раздел в меню слева</p>
        </div>
      </section>
    );
  } else {
    body = (
      <section className="content content--center">
        <h1 className="page-title">{current.title}</h1>
      </section>
    );
  }

  if (!profile) {
    const title = block === 'email_not_confirmed'
      ? 'Подтвердите почту'
      : block === 'access_not_configured'
        ? 'Доступ не настроен, обратитесь к администратору'
        : loadError;
    return (
      <section className="content content--center" aria-busy={!ready}>
        {title ? (
          <div className="greeting">
            <h1>{title}</h1>
            <button type="button" className="btn" onClick={onSignOut}>Войти</button>
          </div>
        ) : null}
      </section>
    );
  }

  return (
    <Shell
      section={section}
      sections={sections}
      user={menuUser}
      showEnvironment={showEnvironment}
      theme={(profile && profile.theme) || 'system'}
      onTheme={changeTheme}
      collapsed={collapsed}
      onToggleCollapsed={() => setCollapsed((value) => !value)}
      onSection={openSection}
      onProfile={() => {
        setScreen('profile');
        const path = sectionPath(section);
        if (window.location.pathname !== path) window.history.pushState(null, '', path);
      }}
      onSignOut={onSignOut}
    >
      {body}
    </Shell>
  );
}

export default App;
